import { useAppContext } from '@/context/app';
import {
  getPathFromState,
  getSearchFromState,
  getStateFromPath,
  getStateFromSearch,
  sortFiltersArray,
  stripHtml,
} from '@/hooks/FilterModal/helpers';
import { useFilterState } from '@/hooks/FilterModal/useFilterState';
import mergeOperations from '@/util/shallowMerge';
import { useLazyQuery } from '@apollo/client';
import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import DEFAULT_OPERATIONS, { STATIC_FILTER_INPUTS } from './filterModal.gql';

const convertStateToNewFilter = state => {
  const newFilter = {};

  for (const [key, valueSet] of state.entries()) {
    if (key === 'price') {
      const rangeValue = Array.from(valueSet)?.[0];

      newFilter['price'] = {
        from: rangeValue[0],
        to: rangeValue[1],
      };
    } else {
      newFilter[key] = {
        in: Array.from(valueSet).flatMap(function (item) {
          return item.value.split(',');
        }),
      };
    }
  }

  return newFilter;
};

export const useFilterModal = props => {
  const {
    categoryId,
    filters = [],
    aggregations,
    sortFields,
    categoryUrlKey,
    DRAWER_NAME,
    handleClose,
    handleOpen,
    isOpen,
  } = props;

  const operations = mergeOperations(DEFAULT_OPERATIONS, props.operations);
  const { getFilterInputsQuery, getCategoryCountQuery } = operations;

  const [isApplying, setIsApplying] = useState(false);
  const [{ drawer }] = useAppContext();
  const [filterState, filterApi] = useFilterState();
  const prevDrawer = useRef(null);

  const router = useRouter();

  const pathname = usePathname();

  const search = useSearchParams();

  const introspectionData = STATIC_FILTER_INPUTS;

  const attributeCodes = useMemo(() => filters.map(({ attribute_code }) => attribute_code), [filters]);

  const [runQuery, { data: categoriesData, loading: loadingCount }] = useLazyQuery(getCategoryCountQuery, {
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first',
  });

  // Create a set of disabled filters.
  const DISABLED_FILTERS = useMemo(() => {
    const disabled = new Set();

    // Disable category filtering when not on a search page.
    if (pathname !== '/search.html') {
      disabled.add('category_id');
      disabled.add('category_uid');
    }

    return disabled;
  }, [pathname]);

  // Get "allowed" filters by intersection of filter attribute codes and
  // schema input field types. This restricts the displayed filters to those
  // that the api will understand.
  const possibleFilters = useMemo(() => {
    const nextFilters = new Set();
    const inputFields = introspectionData ? introspectionData.__type.inputFields : [];

    // perform mapping and filtering in the same cycle
    for (const { name } of inputFields) {
      const isValid = attributeCodes.includes(name);
      const isEnabled = !DISABLED_FILTERS.has(name);

      if (isValid && isEnabled) {
        nextFilters.add(name);
      }
    }

    return nextFilters;
  }, [DISABLED_FILTERS, attributeCodes, introspectionData]);

  const isBooleanFilter = options => {
    const optionsString = JSON.stringify(options);

    return (
      options.length <= 2 &&
      (optionsString.includes(
        JSON.stringify({
          __typename: 'AggregationOption',
          label: '0',
          value: '0',
        }),
      ) ||
        optionsString.includes(
          JSON.stringify({
            __typename: 'AggregationOption',
            label: '1',
            value: '1',
          }),
        ))
    );
  };

  // iterate over filters once to set up all the collections we need
  const [filterNames, filterKeys, filterItems, filterFrontendInput] = useMemo(() => {
    const names = new Map();
    const keys = new Set();
    const frontendInput = new Map();
    const itemsByGroup = new Map();
    // console.log('filters', filters);
    const sortedFilters = sortFiltersArray([...(filters || [])]);

    for (const filter of sortedFilters) {
      const { options, label: name, attribute_code: group } = filter;

      // If this aggregation is not a possible filter, just back out.
      if (possibleFilters.has(group)) {
        const items = [];

        // add filter name
        names.set(group, name);

        // add filter key permutations
        keys.add(`${group}[filter]`);

        // TODO: Get all frontend input type from gql if other filter input types are needed
        // See: https://github.com/magento-commerce/magento2-pwa/pull/26
        if (isBooleanFilter(options)) {
          frontendInput.set(group, 'boolean');
          // add items
          items.push({
            title: 'No',
            value: '0',
            label: name + ':' + 'No',
          });
          items.push({
            title: 'Yes',
            value: '1',
            label: name + ':' + 'Yes',
          });
        } else {
          // Add frontend input type
          frontendInput.set(group, null);

          // add items
          for (const { label, value, count, swatch_data } of options) {
            items.push({
              title: stripHtml(label),
              value,
              count,
              swatch_data: swatch_data,
            });
          }
        }

        itemsByGroup.set(group, items);
      }
    }

    return [names, keys, itemsByGroup, frontendInput];
  }, [filters, possibleFilters]);

  // on apply, write filter state to location
  const isSearchPage = useMemo(() => {
    return pathname === '/search.html';
  }, [pathname]);

  useEffect(() => {
    if (isApplying && aggregations) {
      if (isSearchPage) {
        const nextSearch = getSearchFromState(search, filterKeys, filterState);
        router.push(`${pathname}${nextSearch}`);
        // router.push({
        //   pathname,
        //   search: nextSearch,
        // });
      } else {
        const nextPath = getPathFromState({
          categoryUrlKey,
          aggregations,
          initialPath: pathname,
          filterState,
        });

        router.push(nextPath);
      }

      // mark the operation as complete
      setIsApplying(false);
    }
  }, [filterKeys, filterState, router, isApplying, pathname, search, aggregations, isSearchPage, sortFields, filters]);

  const handleApply = useCallback(() => {
    setIsApplying(true);
    handleClose();
  }, [handleClose]);

  const handleReset = useCallback(() => {
    filterApi.clear();
    setIsApplying(true);
  }, [filterApi, setIsApplying]);

  useEffect(() => {
    if (categoryId) {
      const filters = {
        category_uid: { eq: categoryId },
        ...convertStateToNewFilter(filterState),
      };

      runQuery({
        variables: {
          id: categoryId,
          filters,
        },
        fetchPolicy: 'cache-and-network',
      });
    }
  }, [filterState]);

  useEffect(() => {
    const justOpened = prevDrawer.current === null && drawer === DRAWER_NAME;
    const justClosed = prevDrawer.current === DRAWER_NAME && drawer === null;

    // on drawer toggle, read filter state from location
    if (justOpened || justClosed) {
      const nextState = getStateFromSearch(search, filterKeys, filterItems);

      filterApi.setItems(nextState);
    } else {
      const nextState = getStateFromPath({
        categoryUrlKey,
        aggregations,
        sortFields,
        pathname,
        filters,
      });

      filterApi.setItems(nextState);
    }

    // on drawer close, update the modal visibility state
    if (justClosed) {
      handleClose();
    }

    prevDrawer.current = drawer;
  }, [
    isOpen,
    aggregations,
    sortFields,
    pathname,
    drawer,
    filterApi,
    filterItems,
    filterKeys,
    search,
    handleClose,
    isSearchPage,
    filters,
  ]);

  const handleKeyDownActions = useCallback(
    event => {
      // do not handle keyboard actions when the modal is closed
      if (!isOpen) {
        return;
      }

      switch (event.keyCode) {
        // when "Esc" key fired -> close the modal
        case 27:
          handleClose();
          break;
      }
    },
    [isOpen, handleClose],
  );

  return {
    filterApi,
    filterItems,
    filterKeys,
    filterNames,
    filterFrontendInput,
    filterState,
    handleApply,
    handleClose,
    handleKeyDownActions,
    handleReset,
    isApplying,
    isOpen,
    filterCount: categoriesData?.products.total_count,
    loadingCount,
  };
};
